import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { useAuth } from "../context/AuthContext";
import React, { Fragment, useEffect, useState } from "react";
import { AxisChartColours } from "../util/AxisChartColours";
import "chart.js/auto";
import { Pie } from "react-chartjs-2";
import Row from "react-bootstrap/Row";
import AxisSpinner from "./AxisSpinner";
import { BsDownload, BsArrowClockwise } from "react-icons/bs";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";

export const StatConfirmedShiftsPerTrust = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tallyData, setTallyData] = useState(null);
  const [chartTally, setChartTally] = useState(null);
  const [chartTallyOptions, setChartTallyOptions] = useState(null);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    if (tallyData != null) {
      var backgroundColours = AxisChartColours(tallyData.labels.length);
      setChartTally({
        labels: tallyData.labels,
        datasets: [
          {
            label: "Confirmed Shifts",
            data: tallyData.data,
            backgroundColor: backgroundColours,
            hoverOffset: 50,
          },
        ],
      });
      setChartTallyOptions({
        plugins: {
          tooltip: {
            titleFont: {
              size: 18,
            },
            bodyFont: {
              size: 14,
              weight: "bold",
            },
            backgroundColor: "#00005F",
            borderColor: "#EEEFEF",
            borderWidth: 2,
          },
          legend: {
            labels: {
              font: {
                size: 14,
                weight: "bold",
              },
              color: "#00005F",
            },
          },
        },
      });
    }
  }, [tallyData]);

  const fetch = async () => {
    setLoading(true);

    const config = {
      headers: {
        "content-type": "application/json",
      }
    };
    
    try {
      const response = await authAxios().get(
        "/api/backoffice/stats/confirmed_shifts_per_trust",
        config,
      );

      var labels = [];
      var data = [];
      response.data.forEach((item) => {
        if (item?.tally !== 0) {
          // Using shortName if available, otherwise fallback to full name
          const trustName = item?.trust?.shortName || item?.trust?.name;
          labels.push(trustName);
          data.push(item?.tally);
        }
      });
      setTallyData({
        labels: labels,
        data: data,
      });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log("Error catch");
      console.log(e);
    }
  };

  const getTotalTallyDisplay = (totalTally) => {
    if (totalTally === null || totalTally === undefined) return;
    
    // Sort data by descending value for better readability
    const sortedData = [...totalTally.labels.map((label, index) => ({
      label,
      value: totalTally.data[index]
    }))].sort((a, b) => b.value - a.value);
    
    return (
      <Fragment>
        {sortedData.map((item, i) => (
          <div key={i} className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <div 
                className="color-marker me-2"
                style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: AxisChartColours(sortedData.length)[i],
                  borderRadius: '50%'
                }}
              ></div>
              <span className="h5 mb-0">{item.label}</span>
            </div>
            <div>
              <span className="h5 mb-0">{item.value}</span>
              <Badge bg="light" text="dark" className="ms-2">
                {Math.round((item.value / totalTally.data.reduce((a, b) => a + b, 0)) * 100)}%
              </Badge>
            </div>
          </div>
        ))}
      </Fragment>
    );
  };

  const displayChartTotalTally = (chartTotalTally) => {
    if (chartTotalTally === null || chartTotalTally === undefined) return;
    return (
      <div className="chart-container">
        <Pie
          className={"w-100"}
          data={chartTotalTally}
          options={chartTallyOptions}
        />
      </div>
    );
  };
  
  const handleRefresh = () => {
    fetch();
  };
  
  const handleExportData = () => {
    if (!tallyData || !tallyData.labels || !tallyData.data) {
      alert("No data available to export");
      return;
    }
    
    try {
      // Create CSV content
      let csvContent = "Trust,Confirmed Shifts\n";
      
      // Add each trust and its count
      tallyData.labels.forEach((label, index) => {
        csvContent += `"${label}",${tallyData.data[index]}\n`;
      });
      
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      
      // Create a link element to trigger the download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      // Set link properties
      link.setAttribute("href", url);
      link.setAttribute("download", `confirmed_shifts_per_trust_${new Date().toISOString().split('T')[0]}.csv`);
      
      // Append to document, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Failed to export data. See console for details.");
    }
  };

  const spinnerOrData = () => {
    if (loading) return <AxisSpinner loading={loading} />;
    return (
      <Fragment>
        <div className="d-flex justify-content-end mb-3">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="me-2"
            onClick={handleRefresh}
          >
            <BsArrowClockwise className="me-1" /> Refresh
          </Button>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={handleExportData}
          >
            <BsDownload className="me-1" /> Export
          </Button>
        </div>
        
        <Row xs={1} md={2} className="mt-4 g-4">
          <Col lg={7} className="chart-column">
            {displayChartTotalTally(chartTally)}
          </Col>
          <Col lg={5} className="data-column">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-light">
                <h6 className="mb-0">Confirmed Shifts by Trust</h6>
              </Card.Header>
              <Card.Body className="overflow-auto" style={{ maxHeight: "400px" }}>
                {getTotalTallyDisplay(tallyData)}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {loading ? (
        <div className="text-center py-5">
          <AxisSpinner loading={loading} />
        </div>
      ) : (
        spinnerOrData()
      )}
    </Fragment>
  );
};